import {useSearchParams} from "react-router-dom";
import {useQueryPatch} from "@/hooks/useQueryPatch";
import {useEffect, useMemo, useRef} from "react";
import dayjs from "dayjs";

export type AnalyticsRangeType = "all" | "year" | "month" | "week" | "custom";

function ymd(d: Date) {
    return dayjs(d).format("YYYY-MM-DD");
}

export function useAnalyticsUI() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { patchParams } = useQueryPatch();

    useEffect(() => {
        const hasRange = searchParams.has("rangeType");
        if (hasRange) return;

        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            if (!p.get("rangeType")) p.set("rangeType", "month");
            if (!p.get("start")) p.set("start", dayjs().startOf("month").format("YYYY-MM-DD"));
            if (!p.get("end")) p.set("end", dayjs().endOf("month").format("YYYY-MM-DD"));
            return p;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rangeType = (searchParams.get("rangeType") as AnalyticsRangeType) || "month";

    const defaultStartRef = useRef(dayjs().subtract(1, "year").startOf("day").toDate());
    const defaultEndRef = useRef(dayjs().endOf("day").toDate());

    const startParam = searchParams.get("start");
    const endParam = searchParams.get("end");

    const startYmd = startParam || ymd(defaultStartRef.current);
    const endYmd = endParam || ymd(defaultEndRef.current);

    const startDate = useMemo(() => dayjs(startYmd).toDate(), [startYmd]);
    const endDate = useMemo(() => dayjs(endYmd).toDate(), [endYmd]);

    const actions = {
        setRangeType: (next: AnalyticsRangeType) => {
            if (next === "custom") {
                patchParams({ rangeType: "custom", start: startYmd, end: endYmd }, { replace: true });
                return;
            }

            // ВАЖНО: для all/year/month/week чистим даты, чтобы не путать UI
            patchParams({ rangeType: next, start: null, end: null }, { replace: true });
        },

        setCustomDates: (start: Date, end: Date) => {
            patchParams({ rangeType: "custom", start: ymd(start), end: ymd(end) }, { replace: true });
        },
    };

    return { rangeType, startYmd, endYmd, startDate, endDate, actions };
}
